/**
 * useDraftArtifacts — real-time listener for the project's draft_artifacts subcollection.
 *
 * Returns a sorted array (newest first) of DraftArtifact docs for the active project.
 * Used by the Workspace Canvas surface (WorkspaceCanvasTab) to mount the
 * AI-generated artifact stream from chat (Wave 1 #2 canvas auto-mount).
 *
 * Drafts are TTL-bound on the server (Firestore native `expireAt` policy). The
 * client-side `expireAt` filter is a defensive guard for the (typically
 * sub-minute) window between TTL expiration and physical purge.
 *
 * No `limit` clause: Wave 1 #11 Compare needs the full draft list.
 *
 * Mirrors the shape of useSavedWidgets — the symmetric counterpart for the
 * durable `saved_widgets` collection.
 */

import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebase-config";
import type { DraftArtifact } from "@/types/surfaceArtifacts";
import { logFirestoreListenerError } from "../utils/firestoreListenerDiagnostics";

export function useDraftArtifacts(projectId: string | null | undefined): {
  drafts: DraftArtifact[];
  loading: boolean;
  error: Error | null;
} {
  const [drafts, setDrafts] = useState<DraftArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setDrafts([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    const listenerPath = `projects/${projectId}/draft_artifacts`;
    const q = query(
      collection(db, "projects", projectId, "draft_artifacts"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const nowMs = Date.now();
        const items: DraftArtifact[] = snap.docs
          .map((d) => {
            const data = d.data() as Record<string, unknown>;
            return {
              id: d.id,
              ...data,
            } as DraftArtifact;
          })
          .filter(
            (d) => !d.expireAt || d.expireAt.toMillis() > nowMs
          );

        setDrafts(items);
        setLoading(false);
        setError(null);
      },
      (err) => {
        logFirestoreListenerError({
          listener: "useDraftArtifacts",
          path: listenerPath,
          error: err,
          context: { projectId },
        });
        setError(err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [projectId]);

  return { drafts, loading, error };
}

export default useDraftArtifacts;
